import { Auth0 } from '@client/third-party';
import IdleTimer from '@client/utils/idle-timer';
import apolloClient from '../apollo-provider';
import gql from 'graphql-tag';
import AppActivityStatusDialog from '@client/components/dialogs/activity-status-dialog';
import { EmployeeActivityState } from '@client/constants/employee-activity.ts';
import { updateCurrentEmployeeActivityTypeStoreCache } from '@client/utils/app-helpers';

const TrackerConfig = {
    NO_ACTIVITY_TIMEOUT: window.EMPLOYEE_NO_ACTIVITY_MAX_LIMIT, // 40
    NO_CONFIRMATION_TIMEOUT: window.EMPLOYEE_NO_CONFIRMATION_MAX_LIMIT// 30
 };

class AppTimeTracker {
    /**
     * initializes/clears the time tracker based on the current employee status
     * @param {object} $q the quasar global object //todo: find a way to inject in directly
     */
    async init($q) {
        if (!this.initialized) {
            this.$q = $q;
            this.initTimer(); // initializes the actual time tracker object
            const result = await apolloClient.query({
                query: gql`query{currentEmployeeActivityType}`
            });
            const isActive = result?.data?.currentEmployeeActivityType
            === EmployeeActivityState.WORK;
            if (isActive) {
                this.start(); // start the time tracker
            } else {
                this.stop(); // stop the time tracker
            }
            this.initialized = true;
        }
    }

    initTimer() {
        if (!this.idleTimer) {
            // time without any activity
            const noActivityTimeout = TrackerConfig.NO_ACTIVITY_TIMEOUT;
            // time without any confirmation
            const noConfirmationTimeout = TrackerConfig.NO_CONFIRMATION_TIMEOUT;
            // total timeout for auto logout
            const timeout = Number(noActivityTimeout) + Number(noConfirmationTimeout);
            this.idleTimer = new IdleTimer({
                timeout: timeout,
                // action on reaching a time
                // this action is called every second after the initial timeout is reached
                onTimeReach: {
                    [noActivityTimeout]: ({ elapsed }) => {
                        this.idleTimer?.clearTracker(); // clear the event listeners
                        const remainingTime = timeout - elapsed; // remaining time before timeout
                        // percentage of remaining time before timeout
                        const progressPercentage = +(
                                (remainingTime / noConfirmationTimeout) * 100
                            ).toFixed(2);
                        const progressValue = Math.ceil(Math.max(
                            +remainingTime.toFixed(1),
                            0
                        ));// remaning time
                        if (!this.dialog) {
                            this.dialog = this.$q.dialog({
                                component: AppActivityStatusDialog,
                                progressPercentage,
                                progressValue
                            }).onOk(() => {
                                this.dialog = null;
                                this.idleTimer?.reset();
                            }).onCancel(() => {
                                this.dialog = null;
                                this.stopTracking();
                            });
                        }
                        this.dialog.update({
                            progressPercentage,
                            progressValue
                        });
                    }
                },
                onTimeout: () => {
                    Auth0.logout();
                }
            });
        }
    }

    start() {
        this.idleTimer?.initTimer();
    }

    stop() {
        this.idleTimer?.cleanUp();
    }

    /**
     * set the employee activity status to break,
     * update the apollo cache store and stop the time tracker
     */
    async stopTracking() {
        await apolloClient.mutate({
            mutation: gql`
                mutation SwitchEmployeeActivity($activity: EMPLOYEE_ACTIVITY) {
                    switchEmployeeActivity(activity: $activity){
                        activity
                        isSwitchedActivity
                    }
                }
            `,
            variables: {
                activity: EmployeeActivityState.BREAK
            },
            update: (
                store,
                { data: { switchEmployeeActivity: { activity, isSwitchedActivity } } }
            ) => {
                if (isSwitchedActivity) {
                    updateCurrentEmployeeActivityTypeStoreCache(store, activity);
                }
            }
        });
        this.stop();
    }

    static getInstance() {
      if (!this.instance) {
        this.instance = new AppTimeTracker();
      }
      return this.instance;
    }
}
export default AppTimeTracker.getInstance();
